import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Grid, Button } from "@mui/material";
import AddBox from "@mui/icons-material/AddBox";

// Non-Flying Assignment Form Imports
import { IPairing, IPairingNote } from "@vmosolutions/oc-interfaces";
import { PairingNoteForm } from "../Forms/PairingNoteForm";
import { TextField } from "../../../FormikComponents/TextField";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";

// Display User Info from provided id
import { ShowUserInfo } from "../../../ShowUserInfo/ShowUserInfo";
import { IUser } from "@vmosolutions/oc-interfaces";
import { IEmbeddedFormState } from "../PairingEditorWindow";
import { IRootState } from "src/redux/reducers/CombinedReducers";

interface IPairingNotesTabProps {
  id: string;
  className: string;
  setEmbeddedFormStates: React.Dispatch<React.SetStateAction<IEmbeddedFormState>>;
  initPairing: IPairing;
}

/**
 * Crew Notes tab contains forms used for creation and editing of non-flying assignments and positioning flights
 * @param props -
 */
export const PairingNotesTab = (props: IPairingNotesTabProps): JSX.Element => {
  const { id, className, setEmbeddedFormStates, initPairing } = props;
  const dispatch = useDispatch();
  const [showForm, setShowForm] = useState<boolean>(false);
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[id]);

  // Update the pairingNotes field of the pairing, then hide the add new form.
  const updatePairingNotes = useCallback(
    (pairingNotes: IPairingNote[]): void => {
      dispatch(updatePairingEditor(initPairing._id, { pairingNotes: pairingNotes }));
      setShowForm(false);
    },
    [storedPairing?.pairingNotes, id]
  );

  // Removes end of line notations '//' as content is rendered in textfield in order to display line breaks.
  const parseNote = (note: string): string => note && note.replace(/(?:[//])/g, "");

  // Render Pairing Notes.
  const renderNotes = (): JSX.Element[] => {
    return storedPairing.pairingNotes
      .sort((a, b) => b.createdAt?.valueOf() - a.createdAt?.valueOf())
      .map((note: IPairingNote, index: number) => {
        const dateCreated = new Date(note.createdAt).toDateString();
        return (
          <div key={"pairing_note_" + index} className="pairing_note">
            <TextField
              id={"pairing_note_" + index}
              className="pairing_note_textfield"
              label={
                <>
                  {note?.createdBy && (
                    <ShowUserInfo
                      userId={note.createdBy}
                      getUserLabel={(user: IUser) => `${user.firstName} ${user.lastName}`}
                    />
                  )}
                  {` - ${dateCreated}`}
                </>
              }
              name={`note_+${index}`}
              value={parseNote(note.note)}
              multiline={true}
              minRows="2"
              maxRows="5"
              inputProps={{ readOnly: true }}
            />
          </div>
        );
      });
  };

  return (
    <Grid container direction="column" className={className}>
      <Grid item className="event_actions">
        <Grid container direction="row">
          <Button className="action_btn" onClick={() => setShowForm(true)} disabled={showForm}>
            <AddBox /> Add New
          </Button>
        </Grid>
      </Grid>
      <Grid item>
        {showForm && (
          <PairingNoteForm
            updatePairingNotes={updatePairingNotes}
            setEmbeddedFormStates={setEmbeddedFormStates}
            initPairing={initPairing}
          />
        )}
      </Grid>
      <Grid item className="pairing_notes">
        {renderNotes()}
      </Grid>
    </Grid>
  );
};
